import React from 'react';

type PrivacySection = {
  title: string;
  bullets?: string[];
  paragraph?: string;
};

const sections: PrivacySection[] = [
  {
    title: '1) What FetchTails Does',
    bullets: [
      'FetchTails is a marketplace that connects customers with service providers (clinics/boarding services) for pet care.',
      'We create bookings, generate a verification/OTP code, collect payments, and trigger refunds when eligible.',
    ],
  },
  {
    title: '2) Information We Collect',
    bullets: [
      'Account details: your name, email, and phone number.',
      'Pet details: pet name, pet type/breed, and the notes you share for the service.',
      'Booking details: the service you booked, appointment date/time, provider/service information, address, and any appointment notes.',
      'Service verification: the OTP/verification code and whether it was verified (so the service can be started safely).',
      'Payments: we store payment metadata such as payment status, amount, payment method, and payment reference information. We do not store full card numbers inside the app.',
      'Wallet and refund records: when refunds are applied, or when wallet credits are used as a fallback, those events are recorded so balances and refund history stay correct.',
      'Location (if you choose to use it): we may request location permission to help you find and map nearby services.',
      'Device and usage information: basic app/technical logs used for reliability, security, and debugging.',
    ],
  },
  {
    title: '3) How We Use Your Information',
    bullets: [
      'Create and maintain your account and profile.',
      'Provide booking availability, confirmations, and appointment details to customers and service providers.',
      'Verify service start using OTP/verification data and support the completion flow.',
      'Collect payments and initiate refunds when eligible.',
      'Prevent fraud, payment abuse, and duplicate transactions.',
      'Provide customer support and handle disputes using saved records.',
      'Comply with legal and regulatory obligations.',
    ],
  },
  {
    title: '4) Payments, Wallets, and Refunds',
    bullets: [
      'We send the information needed for payments and refunds to our payment partner.',
      'Refunds: we attempt refunds to the original payment source; if not possible, we use wallet fallback.',
      'Wallet credits: provider holds and customer wallet credits are updated so balances stay accurate.',
      'Card details: we do not store full card numbers inside the app.',
    ],
  },
  {
    title: '5) How We Share Information',
    bullets: [
      'Service providers: booking details needed to perform the service (time, address, and your service request).',
      'Payment partner (Cashfree): payment and refund metadata needed to process payments and refunds.',
      'Platform/hosting and technical providers required to run the app.',
      'Legal compliance: we share information when required by law, court order, or to protect users and the platform.',
    ],
  },
  {
    title: '6) Security',
    bullets: [
      'We use security best practices to protect your data.',
      'Access is restricted using authentication and role-based controls.',
      'We avoid storing sensitive card details. We store payment metadata for operational tracking and refunds.',
    ],
  },
  {
    title: '7) Data Retention',
    bullets: [
      'We keep your information as long as your account is active.',
      'Booking and transaction records can be kept longer for tax, dispute resolution, audits, fraud prevention, and legal compliance.',
    ],
  },
  {
    title: '8) Your Choices and Rights',
    bullets: [
      'You can update your profile information in the app.',
      'You can request access, correction, or deletion of information by contacting FetchTails support.',
      'Some data may still be retained if required by law or for dispute and security purposes.',
    ],
  },
  {
    title: '9) Contact',
    paragraph:
      'For privacy questions, refund-related questions, or data requests, contact FetchTails support with your account details and relevant booking id(s).',
  },
];

const PrivacyPolicy: React.FC = () => (
  <div className="pt-20 pb-16 bg-gradient-to-br from-primary-50 to-white min-h-screen">
    <section className="container-custom max-w-4xl">
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 md:p-8 mb-8">
        <h1 className="text-3xl md:text-4xl font-bold text-secondary-800 mb-4">Privacy Policy</h1>
        <p className="text-sm text-secondary-500 mb-4">Last updated: April 18, 2026</p>
        <p className="text-secondary-700 leading-relaxed">
          This Privacy Policy explains how FetchTails collects, uses, stores, and protects information when you use
          the app. It also explains what we collect for bookings and payments so you can understand how the platform
          works.
        </p>
      </div>

      <div className="space-y-6">
        {sections.map((section) => (
          <article key={section.title} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 md:p-8">
            <h2 className="text-xl md:text-2xl font-bold text-secondary-800 mb-4">{section.title}</h2>
            {section.bullets && (
              <ul className="space-y-3">
                {section.bullets.map((bullet) => (
                  <li key={bullet} className="text-secondary-700 leading-relaxed">
                    • {bullet}
                  </li>
                ))}
              </ul>
            )}
            {section.paragraph && <p className="text-secondary-700 leading-relaxed">{section.paragraph}</p>}
          </article>
        ))}
      </div>
    </section>
  </div>
);

export default PrivacyPolicy;